<template>
  <div v-if="isOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div aria-hidden="true" class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- Modal content -->
      <span aria-hidden="true" class="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
      <div
        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:align-middle sm:max-w-2xl sm:w-full"
      >
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="text-center">
            <h3 id="modal-title" class="text-lg w-full font-medium text-gray-900">
              QR Scanner
            </h3>
            <div v-if="detectedBooking === null" class="mt-2 mx-auto w-full">
              <p class="text-sm text-gray-500" />
              <div class="h-64 w-64 mx-auto bg-white rounded-md shadow overflow-x-auto mt-10">
                <qrcode-stream
                  :formats="['qr_code']"
                  :torch="false"
                  @detect="onDetect"
                  @error="onError"
                />
              </div>
            </div>
          </div>
          <div v-if="detectedBooking !== null" class="mt-2">
            <div v-if="!isToday(detectedBooking.pass_date)" class="p-4 mb-4 text-sm text-red-800 rounded-lg bg-red-50 dark:bg-gray-800 dark:text-red-400" role="alert">
              <span class="font-medium">Attenzione!</span> La data della prenotazione non è quella di oggi.
            </div>
            <div class="overflow-x-auto mt-10">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-gray-50">
                  <tr>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      scope="col"
                    >
                      Campo
                    </th>
                    <th
                      class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      scope="col"
                    >
                      Valore
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap">ID</td>
                    <td class="px-6 py-4 whitespace-nowrap">{{ detectedBooking.id }}</td>
                  </tr>
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap">Nome</td>
                    <td class="px-6 py-4 whitespace-nowrap">{{ detectedBooking.first_name }} {{ detectedBooking.last_name }}</td>
                  </tr>
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap">Email</td>
                    <td class="px-6 py-4 whitespace-nowrap">{{ detectedBooking.email }}</td>
                  </tr>
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap">Data</td>
                    <td class="px-6 py-4 whitespace-nowrap">{{ moment(detectedBooking.pass_date).format("DD MMMM YY") }}</td>
                  </tr>
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap">Quantita' adulti</td>
                    <td class="px-6 py-4 whitespace-nowrap">{{ detectedBooking.qty_adults }}</td>
                  </tr>
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap">Quantita' bambini</td>
                    <td class="px-6 py-4 whitespace-nowrap">{{ detectedBooking.qty_kids }}</td>
                  </tr>
                  <tr v-for="(station, index) in detectedBooking.stations" :key="index">
                    <td class="px-6 py-4 whitespace-nowrap">
                      Ombrellone {{ station.id }} (Lettini {{ station.sunbedCount }}) <br>
                      Extra lettini: {{ station.extraSunbedCount ?? 0 }} <br>
                      Extra adulti: {{ station.extraAdultCount ?? 0 }}
                    </td>
                  </tr>
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap">Totale</td>
                    <td class="px-6 py-4 whitespace-nowrap">{{ detectedBooking.paid_amount }}</td>
                  </tr>
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap">Stato pagamento</td>
                    <td class="px-6 py-4 whitespace-nowrap">{{ detectedBooking.payment_status }}</td>
                  </tr>
                  <tr>
                    <td class="px-6 py-4 whitespace-nowrap">Validato</td>
                    <td class="px-6 py-4 whitespace-nowrap">{{ detectedBooking.validated ? 'Si' : 'No' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm mt-2 px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
            type="button"
            @click="closeModal"
          >
            Chiudi scanner
          </button>
          <button
            :class="canValidate() ? '' : 'cursor-not-allowed opacity-50'"
            v-if="canValidate()"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm mt-2 px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
            type="button"
            @click="validateBooking"
          >
            Convalida
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { QrcodeStream } from "vue-qrcode-reader";
import { useToast } from "vue-toastification";

export default {
  computed: {
    moment() {
      return moment;
    },
  },
  components: {
    QrcodeStream,
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["close", "validate"],
  data() {
    return {
      detectedBooking: null,
    };
  },
  methods: {
    isToday(date) {
      return moment(date).isSame(new Date(), "day");
    },
    canValidate() {
      return (
        this.detectedBooking !== null &&
        this.detectedBooking.validated === false
      );
    },
    closeModal() {
      this.detectedBooking = null;
      this.$emit("close");
    },
    validateBooking() {
      if (this.detectedBooking) {
        this.$emit(
          "validate",
          this.detectedBooking.id,
          this.detectedBooking.token,
        );
        this.closeModal();
      }
    },
    async onDetect(detectedCodes) {
      const detectedBookingJson = JSON.parse(detectedCodes[0].rawValue);
      const toast = useToast();

      try {
        const booking = await axios.get(`/bookings/${detectedBookingJson.id}`, {
          params: {
            token: detectedBookingJson.token,
          },
        });
        this.detectedBooking = booking?.data?.data;
      } catch (error) {
        console.error("Error loading bookings:", error);
        toast.error("Biglietto non trovato");
      }
    },
    onError(error) {
      if (error.name === "NotAllowedError") {
        // user denied camera access permission
      } else if (error.name === "NotFoundError") {
        // no suitable camera device installed
      } else if (error.name === "NotSupportedError") {
        // page is not served over HTTPS (or localhost)
      } else if (error.name === "NotReadableError") {
        // maybe camera is already in use
      } else if (error.name === "OverconstrainedError") {
        // did you request the front camera although there is none?
      } else if (error.name === "StreamApiNotSupportedError") {
        // browser seems to be lacking features
      }
    },
  },
};
</script>
